'use client';

import { addItem } from '@shared/components/cart/actions';
import BDButton from '@shared/components/elements/button';
import { BDStyledDialog } from '@shared/components/elements/dialog';
import { BDSubtitle } from '@shared/components/elements/title';
import { HSection } from '@shared/components/home/section';
import DiamondIcon from '@shared/components/icons/diamond-icon';
import { TotalWithDiscount } from '@shared/components/price-line';
import { CHECKOUT_CART_COOKIE_NAME, QUOTE_CART_COOKIE_NAME } from '@shared/lib/constants';
import { Membership } from '@shared/lib/shopify/types/customer';
import { Cart, DiamondVariant } from '@shared/lib/shopify/types/shopify-native';
import { getDiamondsCostFromCart, groupLinesInCarts } from '@shared/lib/utils/cart-utils';
import { withAddToCartTracking } from '@shared/lib/utils/conversion-tracking';
import { maybeApplyMembershipDiscount } from '@shared/lib/utils/discount-utils';
import { multiplyMoney, renderMoney } from '@shared/lib/utils/money';
import ovalRingImage from '@shared/public/images/diamonds/oval_ring.png';
import radiantRingImage from '@shared/public/images/diamonds/radiant_ring.png';
import roundRingImage from '@shared/public/images/diamonds/round_ring.png';
import roundTinyRingImage from '@shared/public/images/diamonds/round_tiny_ring.png';
import { clsx } from 'clsx';
import { useActionState, useCallback, useMemo, useRef, useState } from 'react';
import Diamond4Cs from './diamond-4-cs';
import DiamondPicker from './diamond-picker';
import FeaturedDiamond from './featured-diamond';
import InCartListItem from './in-cart-list-item';

function AfterAddDiamondDialog({
  isFirstTimeOrder,
  operatingCart,
  dialogOpen,
  setDialogOpen,
}: {
  isFirstTimeOrder: boolean;
  operatingCart: Cart | undefined;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { diamonds = [], rings = [] } = groupLinesInCarts(
    operatingCart,
    operatingCart,
    isFirstTimeOrder
  );
  const { subtotalAmount, totalAmount } = getDiamondsCostFromCart(operatingCart);
  return (
    <BDStyledDialog
      openButton={null}
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
      }}
      title="Your Bonded Diamond™ selection"
      imageOrIcon={
        <div className="bg-bd-contrast flex h-20 w-20 items-center justify-center rounded-full">
          <DiamondIcon className="text-bd-dark h-full w-full p-5" />
        </div>
      }
      description={
        <>
          {isFirstTimeOrder ? 'Quoted Total: ' : 'Total: '}
          <TotalWithDiscount subtotalAmount={subtotalAmount} totalAmount={totalAmount} />, or{' '}
          {renderMoney(multiplyMoney(totalAmount, 0.25))} + tax monthly for 4 month if you choose
          our no credit check 4 easy payment plan.{' '}
          {isFirstTimeOrder && 'Quote only, no payment nor commitment required today.'}
        </>
      }
      panelClassName="!max-w-4xl"
    >
      <div className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        {diamonds.map((lineItem) => (
          <InCartListItem
            key={lineItem.id}
            lineItem={lineItem}
            operatingCart={operatingCart!}
            isFirstTimeOrder={isFirstTimeOrder}
          />
        ))}
      </div>
      <div className="mt-8 flex flex-col items-center justify-center gap-x-4 gap-y-2 sm:flex-row sm:flex-wrap">
        <BDButton
          styleType="secondary"
          className="min-w-65 mx-2 w-full px-4 py-2 capitalize sm:mx-0 sm:w-auto"
          onClick={() => {
            setDialogOpen(false);
          }}
        >
          Keep browsing
        </BDButton>
        <BDButton
          styleType={rings.length > 0 ? 'primary' : 'secondary'}
          className="min-w-65 mx-2 w-full px-4 py-2 capitalize sm:mx-0 sm:w-auto"
          href="/shop/confirmation"
          disabled={diamonds.length === 0}
        >
          Review my cart
        </BDButton>
        <BDButton
          styleType={rings.length > 0 ? 'secondary' : 'primary'}
          className="min-w-65 mx-2 w-full px-4 py-2 capitalize sm:mx-0 sm:w-auto"
          href="/shop/jewelry"
          disabled={diamonds.length === 0}
        >
          Find a Jewelry
        </BDButton>
      </div>
    </BDStyledDialog>
  );
}

const bestSellerDiamonds = [
  {
    name: 'Round 1/4 Carat',
    ringName: 'Vienna V-Split 6-Stone Ring',
    image: roundTinyRingImage,
    carat: 0.25,
    shape: 'Round',
  },
  {
    name: 'Round 1 Carat',
    ringName: 'Bordeaux Étoile 6-Prong Solitaire Ring',
    image: roundRingImage,
    carat: 1,
    shape: 'Round',
  },
  {
    name: 'Oval 2 Carat',
    ringName: 'Lyon Classic Solitaire Ring',
    image: ovalRingImage,
    carat: 2,
    shape: 'Oval',
  },
  {
    name: 'Radiant 3 Carat',
    ringName: 'Cannes Lumière Solitaire Ring',
    image: radiantRingImage,
    carat: 3,
    shape: 'Radiant',
  },
];

function BestSellers({
  cutVariantLists,
  isFirstTimeOrder,
  formActionFunction,
  setSelectedVariant,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
  isFirstTimeOrder: boolean;
  formActionFunction: (prevState: any, formData: any) => Promise<string | undefined>;
  setSelectedVariant: (variant: DiamondVariant) => void;
}) {
  return (
    <HSection id="featured">
      <BDSubtitle>Best Sellers</BDSubtitle>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {bestSellerDiamonds.map((diamond) => {
          const variant = cutVariantLists[diamond.shape]?.find((v) => v.carat === diamond.carat);
          if (!variant) {
            return null;
          }
          return (
            <FeaturedDiamond
              key={diamond.name}
              name={diamond.name}
              ringName={diamond.ringName}
              image={diamond.image}
              variant={variant}
              isFirstTimeOrder={isFirstTimeOrder}
              formActionFunction={formActionFunction}
              setSelectedVariant={setSelectedVariant}
            />
          );
        })}
      </div>
    </HSection>
  );
}

export default function AddDiamond({
  cutVariantLists,
  operatingCart,
  isFirstTimeOrder,
  membership,
  shape,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
  operatingCart: Cart | undefined;
  isFirstTimeOrder: boolean;
  membership: Membership | undefined;
  shape: string | null;
}) {
  const [selectedShape, setSelectedShape] = useState<string | null>(shape || 'Round');
  const [selectedVariant, setSelectedVariant] = useState<DiamondVariant | null>(null);
  const [afterAddDiamondDialogOpen, setAfterAddDiamondDialogOpen] = useState(false);

  const formActionFunction = useCallback(
    async (prevState: any, formData: any) => {
      const { error: addItemError, result: newCart } = await addItem(
        {
          cartId: operatingCart?.id,
          cartCookieName: isFirstTimeOrder ? QUOTE_CART_COOKIE_NAME : CHECKOUT_CART_COOKIE_NAME,
          variantId: selectedVariant!.id,
          quantity: 1,
        },
        prevState,
        formData
      );
      if (addItemError) {
        return addItemError;
      }

      const { error: applyDiscountError } = await maybeApplyMembershipDiscount(newCart, membership);

      if (applyDiscountError) {
        console.warn(`Fail to auto apply membership discount error: ${applyDiscountError}`);
      }

      // Success, clear state, open dialog
      setAfterAddDiamondDialogOpen(true);
    },
    [operatingCart?.id, isFirstTimeOrder, membership, selectedVariant, setAfterAddDiamondDialogOpen]
  );

  const trackedFormActionFunction = useMemo(
    () => withAddToCartTracking(formActionFunction, [selectedVariant!]),
    [formActionFunction, selectedVariant]
  );

  const [errorMessage, formAction, pending] = useActionState(formActionFunction, null);
  const trackedFormAction = useMemo(
    () => withAddToCartTracking(formAction, [selectedVariant!]),
    [formAction, selectedVariant]
  );

  const formRef = useRef<HTMLFormElement>(null);
  return (
    <>
      <BestSellers
        cutVariantLists={cutVariantLists}
        isFirstTimeOrder={isFirstTimeOrder}
        formActionFunction={trackedFormActionFunction}
        setSelectedVariant={setSelectedVariant}
      />
      <HSection id="customize">
        <form className="flex flex-col gap-6" action={trackedFormAction} ref={formRef}>
          <BDSubtitle className="hidden md:block">Customize your Bonded Diamond™</BDSubtitle>
          <DiamondPicker
            cutVariantLists={cutVariantLists}
            onSelectShape={setSelectedShape}
            onSelectVariant={setSelectedVariant}
            showQuantity={true}
            preselectedShape={shape}
          />
          <div className="relative mb-8 flex flex-col items-end">
            <BDButton
              styleType="primary"
              className={clsx(
                'relative flex w-full items-center justify-center px-8 py-2 capitalize sm:w-auto'
              )}
              disabledMessage="Please select a diamond shape and carat weight."
              type="submit"
              disabled={pending || !selectedVariant}
              pending={pending}
              aria-busy={pending}
            >
              Add to cart
            </BDButton>
            {errorMessage && <p className="mt-2 text-xs text-red-500">{errorMessage}</p>}
            {/* TODO decide if we shall show cart info here. */}
            <AfterAddDiamondDialog
              isFirstTimeOrder={isFirstTimeOrder}
              operatingCart={operatingCart}
              dialogOpen={afterAddDiamondDialogOpen}
              setDialogOpen={setAfterAddDiamondDialogOpen}
            />
          </div>
        </form>
      </HSection>

      <HSection bgClassName="bg-bd-secondary">
        <BDSubtitle>Your Bonded Diamond™ Guarantees</BDSubtitle>
        <p>
          Your Bonded Diamond™ comes with an IGI certificate of authenticity and quality assurance.
          We follow the classic 4Cs of diamond quality: cut, color, clarity, and carat.
        </p>
        <Diamond4Cs
          sizeVariants={cutVariantLists[selectedShape!]!}
          selectedVariant={selectedVariant!}
        />
      </HSection>
    </>
  );
}
